import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const OfferBanner = () => {
  const [banners, setBanners]   = useState([]);
  const [current, setCurrent]   = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading]   = useState(true);
  const timerRef = useRef(null);

  // ── Responsive check ────────────────────────────────────
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Fetch banners from DB ────────────────────────────────
  useEffect(() => {
    fetch(`${API}/admin/banners/public`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.banners.length > 0) {
          setBanners(data.banners);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // ── Auto-play timer ──────────────────────────────────────
  const startTimer = () => {
    clearInterval(timerRef.current);
    if (banners.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent(p => (p + 1) % banners.length);
    }, 5000);
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [banners]);

  const goTo = (i) => {
    setCurrent(i);
    startTimer();
  };

  // ── Empty / loading states ───────────────────────────────
  if (loading) {
    return <div style={{ width: '100%', height: 480, background: '#1C1A18' }} />;
  }

  if (banners.length === 0) return null;

  const slide = banners[current];
  const normalizedLink = slide.link_url
    ? slide.link_url.replace(/^\/collections\/(.+)$/i, (_, slugPart) => {
        const cleanSlug = slugPart.trim().replace(/\s+/g, '-').toLowerCase();
        return `/collections/${cleanSlug}`;
      })
    : '';
  const isInternalLink = normalizedLink.startsWith('/');

  return (
    <>
      <style>{`
        
        .ob-wrap {
          width: 100%;
          position: relative;
          overflow: hidden;
        }
              .ob-slide {
          position: relative;
          inset: unset;
          width: 100%;
          display: block;
        }
                
                .ob-slide img {
          width: 100%;
          height: auto;
          display: block;
        }
        .ob-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #B08D57;
          z-index: 10;
          transform-origin: left;
        }
        .ob-dots {
          position: absolute;
          bottom: 18px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
          z-index: 10;
        }
        .ob-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: rgba(255,255,255,0.4);
          border: none;
          cursor: pointer;
          padding: 0;
          transition: background 0.3s, transform 0.3s;
        }
        .ob-dot.active {
          background: #B08D57;
          transform: scale(1.4);
        }
        .ob-prev,
        .ob-next {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          background: rgba(28,26,24,0.55);
          border: 1px solid rgba(176,141,87,0.4);
          color: #D4AF78;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-family: serif;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
          line-height: 1;
        }
        .ob-prev { left: 20px; }
        .ob-next { right: 20px; }
        .ob-prev:hover,
        .ob-next:hover {
          background: #B08D57;
          border-color: #B08D57;
          color: #fff;
        }

      @media (max-width: 768px) {
        .ob-prev,
        .ob-next { display: none; }
      }
      `}</style>

      <div className="ob-wrap">

        {/* Slides */}
        <AnimatePresence mode="wait">
          {isInternalLink ? (
           <motion.div
            key={slide.id}
            className="ob-slide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: 'easeInOut' }}
          >
            <Link to={normalizedLink} style={{ display: 'block', width: '100%', height: '100%' }}>
                <img
                  src={slide.image_url}
                  alt={slide.title || `Banner ${current + 1}`}
                />
              </Link>
            </motion.div>
          ) : (
            <motion.a
              key={slide.id}
              className="ob-slide"
              href={slide.link_url || '#'}
              onClick={e => { if (!slide.link_url) e.preventDefault(); }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: 'easeInOut' }}
            >
              <img
                src={slide.image_url}
                alt={slide.title || `Banner ${current + 1}`}
              />
            </motion.a>
          )}
        </AnimatePresence>

        {/* Progress bar — only show when multiple banners */}
        {banners.length > 1 && (
          <motion.div
            className="ob-progress"
            key={current}
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 5, ease: 'linear' }}
          />
        )}

        {/* Prev arrow — only show when multiple banners */}
        {banners.length > 1 && (
          <button
            className="ob-prev"
            onClick={() => goTo((current - 1 + banners.length) % banners.length)}
            aria-label="Previous"
          >
            &#8592;
          </button>
        )}

        {/* Next arrow */}
        {banners.length > 1 && (
          <button
            className="ob-next"
            onClick={() => goTo((current + 1) % banners.length)}
            aria-label="Next"
          >
            &#8594;
          </button>
        )}

        {/* Dots */}
        {banners.length > 1 && (
          <div className="ob-dots">
            {banners.map((_, i) => (
              <button
                key={i}
                className={`ob-dot${i === current ? ' active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}

      </div>
    </>
  );
};

export default OfferBanner;